import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Loader2, 
  StickyNote,
  Search,
  Filter,
  ChevronDown,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import NoteCard from './NoteCard';
import NoteForm from './NoteForm';
import { 
  Note, 
  NoteCreate, 
  NoteUpdate, 
  NoteType, 
  EntityType,
  getNoteTypeLabel 
} from '@/app/types/note';
import { 
  getNotesForEntity, 
  createNote, 
  updateNote, 
  deleteNote, 
  toggleNotePin,
  countNotesForEntity 
} from '@/app/services/noteService';
import { cn } from '@/lib/utils';

interface NotesPanelProps {
  entityType: EntityType;
  entityId: number;
  title?: string;
  className?: string;
  compact?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  maxHeight?: string;
  emptyMessage?: string;
}

export default function NotesPanel({
  entityType,
  entityId,
  title = 'Notes',
  className,
  compact = false,
  showSearch = true,
  showFilters = true,
  maxHeight = '600px',
  emptyMessage = 'No notes yet. Add your first note to get started.'
}: NotesPanelProps) {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<NoteType | 'all'>('all');
  const [showPrivateOnly, setShowPrivateOnly] = useState(false);

  const queryKey = ['notes', entityType, entityId];

  // Fetch notes
  const { data: notes = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => getNotesForEntity(entityType, entityId),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Create note mutation
  const createMutation = useMutation({
    mutationFn: (data: NoteCreate) => createNote(data),
    onSuccess: () => {
      toast.success('Note added successfully');
      queryClient.invalidateQueries({ queryKey });
      setShowAddDialog(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    },
  });

  // Update note mutation
  const updateMutation = useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: NoteUpdate }) => 
      updateNote(noteId, data),
    onSuccess: () => {
      toast.success('Note updated successfully');
      queryClient.invalidateQueries({ queryKey });
      setEditingNote(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update note');
    },
  });

  // Delete note mutation
  const deleteMutation = useMutation({
    mutationFn: (noteId: number) => deleteNote(noteId),
    onSuccess: () => {
      toast.success('Note deleted successfully');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    },
  });

  // Toggle pin mutation
  const togglePinMutation = useMutation({
    mutationFn: (noteId: number) => toggleNotePin(noteId),
    onSuccess: () => {
      toast.success('Note pin status updated');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update pin status');
    },
  });

  // Filter notes based on search and filters
  const filteredNotes = notes.filter((note) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        note.title?.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query) ||
        note.extra_data?.tags?.some((tag: string) => 
          tag.toLowerCase().includes(query)
        );
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filterType !== 'all' && note.note_type !== filterType) {
      return false;
    }

    // Private filter
    if (showPrivateOnly && !note.is_private) {
      return false;
    }

    return true;
  });

  // Sort notes: pinned first, then by created_at desc
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) {
      return a.is_pinned ? -1 : 1;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const handleCreateNote = (data: NoteCreate) => {
    createMutation.mutate(data);
  };

  const handleUpdateNote = (data: NoteUpdate) => {
    if (editingNote) {
      updateMutation.mutate({ noteId: editingNote.id, data });
    }
  };

  const handleDeleteNote = (noteId: number) => {
    if (confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      deleteMutation.mutate(noteId);
    }
  };

  const handleTogglePin = (noteId: number) => {
    togglePinMutation.mutate(noteId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setShowPrivateOnly(false);
  };

  const hasActiveFilters = searchQuery || filterType !== 'all' || showPrivateOnly;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load notes. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">{title}</h3>
          <Badge variant="secondary" className="ml-2">
            {notes.length}
          </Badge>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          size="sm"
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Note
        </Button>
      </div>

      {/* Search and Filters */}
      {(showSearch || showFilters) && notes.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2">
          {showSearch && (
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          
          {showFilters && (
            <div className="flex gap-2">
              <Select 
                value={filterType} 
                onValueChange={(value) => setFilterType(value as NoteType | 'all')}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <Separator className="my-1" />
                  {Object.values(NoteType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {getNoteTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56">
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm">Filters</h4>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="private-only"
                        checked={showPrivateOnly}
                        onChange={(e) => setShowPrivateOnly(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="private-only" className="text-sm">
                        Private notes only
                      </label>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Notes List */}
      <ScrollArea className="w-full" style={{ maxHeight }}>
        <div className="space-y-3 pr-4">
          {sortedNotes.length > 0 ? (
            sortedNotes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={setEditingNote}
                onDelete={handleDeleteNote}
                onTogglePin={handleTogglePin}
                compact={compact}
              />
            ))
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <StickyNote className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground text-center">
                  {hasActiveFilters 
                    ? 'No notes match your filters.' 
                    : emptyMessage}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="mt-4"
                  >
                    Clear filters
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>

      {/* Add Note Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a new note to this {entityType}.
            </DialogDescription>
          </DialogHeader>
          <NoteForm
            entityType={entityType}
            entityId={entityId}
            mode="create"
            onSubmit={handleCreateNote}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={!!editingNote} onOpenChange={() => setEditingNote(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>
              Update the note details below.
            </DialogDescription>
          </DialogHeader>
          {editingNote && (
            <NoteForm
              entityType={entityType}
              entityId={entityId}
              note={editingNote}
              mode="edit"
              onSubmit={handleUpdateNote}
              onCancel={() => setEditingNote(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}